import React from 'react';
import { useTranslation } from 'react-i18next';

import dayjs from 'shared/helpers/dayjs';
import { Arrow, General } from 'icons/untitledui';
import { useUser } from 'shared/hooks/api/user';
import { PendingUpload } from 'shiftNotes/helpers/ShiftNoteCaptureProvider';

import * as StackStyled from 'shiftNotes/styles/NotesStack.style';
import * as Styled from 'shiftNotes/styles/NoteCaptureProcessing.style';

export interface NoteCaptureProcessingProps {
  inline?: boolean;
  upload: PendingUpload;
  onRetry?: (id: number) => void;
  onDismiss?: (id: number) => void;
}

export default function NoteCaptureProcessing({
  inline = false,
  upload,
  onRetry,
  onDismiss,
}: NoteCaptureProcessingProps): JSX.Element {
  const { t, i18n } = useTranslation('pageShiftNotes');
  const { data: user } = useUser();

  const authorName = user ? `${user.first_name} ${user.last_name}`.trim() : '';
  const time = dayjs(upload.createdAt).format('MMM D, h:mm A');
  const isError = upload.status === 'error';
  const noteText =
    !(upload.data instanceof FormData) && upload.data.text_note
      ? upload.data.text_note
      : null;

  const rawErrorMessage = upload.error?.message?.trim() ?? '';
  const translatedErrorKey = rawErrorMessage
    ? `noteCaptureCard.errorReasons.${rawErrorMessage}`
    : null;
  const hasTranslatedError = translatedErrorKey
    ? i18n.exists(translatedErrorKey, { ns: 'pageShiftNotes' })
    : false;
  let errorReason: string | null = null;
  if (rawErrorMessage) {
    errorReason = hasTranslatedError
      ? t(translatedErrorKey!)
      : t('noteCaptureCard.errorReasonGeneric');
  }

  return (
    <Styled.Wrapper $inline={inline}>
      <StackStyled.Card as="div">
        <StackStyled.Header>
          <StackStyled.HeaderAuthor>
            <StackStyled.AuthorName>{authorName}</StackStyled.AuthorName>
            <StackStyled.TimeLabel>{time}</StackStyled.TimeLabel>
          </StackStyled.HeaderAuthor>
        </StackStyled.Header>
        {noteText && <StackStyled.Body>{noteText}</StackStyled.Body>}
        {isError ? (
          <Styled.ErrorPill role="alert">
            <Styled.ErrorPillBody>
              <Styled.ErrorPillText>
                <Styled.ErrorPillLabel>
                  {upload.inputType === 'microphone'
                    ? t('noteCaptureCard.errorAudio')
                    : t('noteCaptureCard.errorText')}
                </Styled.ErrorPillLabel>
                {errorReason && (
                  <Styled.ErrorPillReason>{errorReason}</Styled.ErrorPillReason>
                )}
              </Styled.ErrorPillText>
              <Styled.ErrorActions>
                {onDismiss && (
                  <Styled.DismissAction
                    $size="tiny"
                    type="button"
                    onClick={() => onDismiss(upload.id)}
                  >
                    <General.XClose />
                    {t('noteCaptureCard.dismiss')}
                  </Styled.DismissAction>
                )}
                {onRetry && (
                  <Styled.RetryAction
                    $size="tiny"
                    type="button"
                    onClick={() => onRetry(upload.id)}
                  >
                    <Arrow.RefreshCcw01 />
                    {t('noteCaptureCard.tryAgain')}
                  </Styled.RetryAction>
                )}
              </Styled.ErrorActions>
            </Styled.ErrorPillBody>
          </Styled.ErrorPill>
        ) : (
          <StackStyled.LoadingPill role="status" aria-live="polite">
            <StackStyled.LoadingPillIcon aria-hidden="true">
              <General.Loading02 />
            </StackStyled.LoadingPillIcon>
            <StackStyled.LoadingPillLabel>
              {t('noteCaptureCard.processing')}
            </StackStyled.LoadingPillLabel>
          </StackStyled.LoadingPill>
        )}
      </StackStyled.Card>
    </Styled.Wrapper>
  );
}
